import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import classnames from 'classnames';
import dropin from 'braintree-web-drop-in';
import goxApi from 'api/gox-api';
import styles from './Braintree.module.scss';
import AlertStore from 'stores/Alert';

const Braintree = forwardRef((props, ref) => {

  const [ paymentInProgress, setPaymentInProgress ] = useState(false);
  const instanceRef = useRef(null);
  const cancelRef = useRef(null);

  useImperativeHandle(ref, () => ({
    init: () => init()
  }));

  function teardown() {
    if (instanceRef.current) {
      instanceRef.current.teardown();
      instanceRef.current = null;
    }
    setPaymentInProgress(false);
  }

  // user abandoned the flow (e.g. closed the venmo window) — unblock the caller
  function cancel() {
    const reject = cancelRef.current;
    cancelRef.current = null;
    teardown();
    if (reject) {
      reject(new Error('Payment method setup cancelled'));
    }
  }

  function init() {
    return new Promise(async (resolve, reject) => {
      cancelRef.current = reject;
      try {
        const { client_token } = await goxApi.braintreeClientToken();

        // the container must be visible before drop-in mounts — venmo's
        // QR/login views can mis-render inside a hidden subtree
        setPaymentInProgress(true);

        const instance = await dropin.create({
          authorization: client_token,
          container: '#braintree',
          dataCollector: true,
          applePay: {
            displayName: 'Cheetah X Inc.',
            paymentRequest: {
              total: {
                label: 'Cheetah X Inc.',
                amount: '1.00'
              }
            }
          },
          venmo: {
            paymentMethodUsage: 'multi_use',
            allowDesktopWebLogin: true,
            allowDesktop: true,
            mobileWebFallBack: true
          }
        });

        instanceRef.current = instance;

        instance.on('paymentMethodRequestable', () => {
          instance.requestPaymentMethod(async (err, payload) => {
            try {
              if (err) {
                throw err;
              }
              await goxApi.addCard(payload?.nonce, payload?.deviceData);
              cancelRef.current = null;
              teardown();
              resolve();
            } catch (err) {
              cancelRef.current = null;
              teardown();
              reject(err);
            }
          });
        });
      } catch (err) {
        AlertStore.add(err.message);
        cancelRef.current = null;
        teardown();
        reject(err);
      }
    });

  }

  return (
    <div className={classnames(styles.container, {
      [styles.visible]: paymentInProgress
    })}>
      <button type="button" className={styles.close} onClick={cancel}>Close</button>
      <div id="braintree" />
    </div>
  )
});

export default Braintree;
